import React, { useEffect, useState, useRef } from 'react';
import { showInfoAlert } from '../utils/alertFunction';
import '../styles/voiceinput.css';

interface VoiceInputProps {
    onTranscript: (text: string) => void;
    disabled?: boolean;
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscript, disabled }) => {
    const [isListening, setIsListening] = useState(false);
    const [volume, setVolume] = useState(0);
    const [wasVoiceInput, setWasVoiceInput] = useState(false);
    const [showPermissionPopup, setShowPermissionPopup] = useState(false);
    const [hasPermission, setHasPermission] = useState<boolean | null>(null);
    const recognitionRef = useRef<any>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const audioContext = useRef<AudioContext | null>(null);
    const analyser = useRef<AnalyserNode | null>(null);
    const mediaStream = useRef<MediaStream | null>(null);
    const animationFrame = useRef<number | null>(null);

    // Sayfa yüklendiğinde mikrofon izni durumunu kontrol et
    useEffect(() => {
        const checkPermission = async () => {
            try {
                const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                setHasPermission(permissionStatus.state === 'granted');

                permissionStatus.onchange = () => {
                    setHasPermission(permissionStatus.state === 'granted');
                };
            } catch (error) {
                console.error('İzin durumu kontrol edilemedi:', error);
            }
        };

        checkPermission();
    }, []);

    const stopListening = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        if (mediaStream.current) {
            mediaStream.current.getTracks().forEach(track => track.stop());
        }
        if (audioContext.current && audioContext.current.state !== 'closed') {
            audioContext.current.close();
        }
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsListening(false);
        setVolume(0);
        setWasVoiceInput(false);
    };

    const handlePermissionGranted = async () => {
        setShowPermissionPopup(false);
        setHasPermission(true);
        await startListening();
    };

    const handlePermissionDenied = () => {
        setShowPermissionPopup(false);
        setHasPermission(false);
        showInfoAlert('Bilgilendirme', 'Mikrofon izni verilmediği için sesli mesaj özelliği kullanılamıyor. İzin vermek için tekrar deneyebilirsiniz.');
    };

    const startListening = async () => {
        // GUID kontrolü
        const chatGuid = sessionStorage.getItem('chatGuid');
        if (!chatGuid) {
            showInfoAlert('Bilgilendirme', 'Lütfen önce bir kategori seçiniz.');
            return;
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            showInfoAlert('Bilgilendirme', 'Tarayıcınız ses tanıma özelliğini desteklemiyor.');
            return;
        }

        try {
            // Ses analizi için ayarlar
            mediaStream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioContext.current = new AudioContext();
            analyser.current = audioContext.current.createAnalyser();
            analyser.current.fftSize = 256;
            analyser.current.smoothingTimeConstant = 0.8;

            const source = audioContext.current.createMediaStreamSource(mediaStream.current);
            source.connect(analyser.current);

            // Ses seviyesi kontrolü
            const updateVolume = () => {
                const dataArray = new Uint8Array(analyser.current!.frequencyBinCount);
                analyser.current?.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                setVolume(Math.min(average / 50, 2));
                animationFrame.current = requestAnimationFrame(updateVolume);
            };
            updateVolume();

            // Speech Recognition ayarları
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'tr-TR';

            let fullTranscript = '';

            recognitionRef.current.onresult = (event: any) => {
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        fullTranscript += transcript + ' ';
                    } else {
                        interimTranscript += transcript;
                    }
                }

                onTranscript(fullTranscript + interimTranscript);

                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                timeoutRef.current = setTimeout(() => {
                    stopListening();
                    const sendButton = document.querySelector('.btn.btn-light.text-secondary i.fas.fa-paper-plane')?.parentElement as HTMLButtonElement;
                    if (sendButton) {
                        sendButton.click();
                    }
                }, 1500);
            };

            recognitionRef.current.start();
            setIsListening(true);
            setWasVoiceInput(true);
            (window as any).wasVoiceInput = true;

        } catch (error) {
            console.error('Mikrofon erişim hatası:', error);
            if (error instanceof Error && error.name === 'NotAllowedError') {
                setShowPermissionPopup(true);
            } else {
                showInfoAlert('Bilgilendirme', 'Mikrofon erişim hatası. Lütfen tarayıcınızın veya cihazınızın ses tanıma özelliklerini kontrol edin.');
            }
        }
    };

    useEffect(() => {
        return () => {
            window.speechSynthesis.cancel();
            stopListening();
            setWasVoiceInput(false);
        };
    }, []);

    // Metni sesli okumak için fonksiyon
    const speakResponse = (text: string) => {
        if (!wasVoiceInput) return;

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'tr-TR';

        // Okuma bittiğinde
        utterance.onend = () => {
            console.log('Okuma tamamlandı');
        };

        // Hata durumunda
        utterance.onerror = (event) => {
            console.error('Okuma hatası:', event);
        };

        // Okuma başladığında
        utterance.onstart = () => {
            console.log('Okuma başladı');
        };

        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
    };

    // Global olarak erişilebilir yap
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).speakResponse = speakResponse;
        }
    }, [wasVoiceInput]);

    return (
        <>
            <button
                onClick={() => {
                    if (!isListening) {
                        if (hasPermission === true) {
                            startListening();
                        } else {
                            setShowPermissionPopup(true);
                        }
                    } else {
                        stopListening();
                    }
                }}
                className="chat-inputbar-iconbtn"
                title={isListening ? 'Dinlemeyi Durdur' : 'Sesli Mesaj'}
                type="button"
                disabled={disabled}
            >
                <img src="/images/voice-icon.svg" alt="Sesli Mesaj" width="24" height="24" />
            </button>

            {isListening && (
                <div className="voice-popup" onClick={stopListening} style={{ cursor: 'pointer' }}>
                    <div
                        className="voice-visualizer"
                        style={{
                            transform: `scale(${1 + volume * 0.5})`,
                            transition: 'transform 0.1s ease'
                        }}
                    >
                        <i className="fas fa-microphone" />
                    </div>
                    <div className="voice-status">Dinlemeyi durdurmak için tıklayın</div>
                </div>
            )}

            {showPermissionPopup && (
                <div className="fullscreen-popup">
                    <div className="popup-content">
                        <div className="icon-container">
                            <img src="/images/microphone-icon.svg" alt="Mikrofon İzni" />
                        </div>
                        <div className="popup-texts">
                            <h3 className="popup-title">Mikrofon İzni</h3>
                            <p className="popup-desc">
                                Sesli mesaj gönderebilmek için mikrofon izni vermeniz gerekmektedir.
                            </p>
                        </div>
                        <div className="button-container">
                            <button className="btn btn-approve" onClick={handlePermissionGranted}>
                                Onayla
                            </button>
                            <button className="btn btn-cancel" onClick={handlePermissionDenied}>
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VoiceInput;